import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TradeInsightsPanel from '../insights/TradeInsightsPanel';
import PageHeading from '../PageHeading';
import TradeIntegrationModal from './TradeIntegrationModal';
import AdminAccountsOverview from './AdminAccountsOverview';
import ClientAccountPicker from './ClientAccountPicker';
import ClientReportingEquityDonut from './ClientReportingEquityDonut';
import ClientReportingWorldMarkets from './ClientReportingWorldMarkets';
import ClientReportingMarketHours from './ClientReportingMarketHours';
import ClientReportingStocks from './ClientReportingStocks';
import ClientReportingRecentTrades from './ClientReportingRecentTrades';
import ClientReportingTradeCalendar from './ClientReportingTradeCalendar';
import ClientReportingPortfolioTable from './ClientReportingPortfolioTable';
import ClientReportingPortfolioDetail from './ClientReportingPortfolioDetail';
import ClientReportingChartPanel from './ClientReportingChartPanel';
import ClientReportingChartColorsAdmin from './ClientReportingChartColorsAdmin';
import ClientReportingToasts from './ClientReportingToasts';
import { fetchMergedClientPerformance, brokerAccountPickerLabel, parseTargetUserId } from './clientReportingUtils';
import { CHART_TABS, buildTabChartData, tabValueFormat } from './seriesTransforms';
import { readClientReportingCache, writeClientReportingCache } from './clientReportingCache';
import usePersistentTab from '../../hooks/usePersistentTab';
import {
  fetchClients,
  fetchClientSummary,
  fetchNinjaTraderSyncJob,
  brokerSync,
  resyncAllBrokers,
  resyncAccount,
  fetchClientReportingPrefs,
  saveDefaultPortfolio,
  fetchConnectionHealth,
  fetchClientReportingChartColors,
  snaptradeSync,
} from '../../services/vectorDashboardApi';
import { refreshMyAccounts } from '../dashboard/myData/myDataApi';
import {
  chartColorsToCssVars,
  DEFAULT_CLIENT_REPORTING_CHART_COLORS,
} from './clientReportingBenchmarkConfig';
import { resolveBrokerKey } from './brokerBranding';
import ClientReportingReauthBanner, { formatLastSynced } from './ClientReportingReauthBanner';
import ClientReportingDashboardHeader from './ClientReportingDashboardHeader';
import { HoverTip, useHoverTip } from './clientReportingHoverTip';
import useClientReportingSidebarQuotes from './useClientReportingSidebarQuotes';

/** Cap portfolio-table summary prefetches — typical users have 2–5 linked accounts. */
const MAX_PORTFOLIO_TABLE_SUMMARIES = 8;

function accountQueryParam(clientId) {
  const params = new URLSearchParams(window.location.search);
  if (clientId) params.set('account', clientId);
  else params.delete('account');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

const TRADOVATE_SYNC_STORAGE_KEY = 'risklo_tradovate_sync_job';
const TRADOVATE_SYNC_STALE_MS = 4 * 60 * 1000;
/** How long a success toast stays up. Long enough to read a trade count twice. */
const NOTICE_DISMISS_MS = 6000;

function readStoredTradovateSyncJob() {
  try {
    const raw = sessionStorage.getItem(TRADOVATE_SYNC_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.jobId) return null;
    if (parsed.startedAt && Date.now() - parsed.startedAt > TRADOVATE_SYNC_STALE_MS) {
      sessionStorage.removeItem(TRADOVATE_SYNC_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function storeTradovateSyncJob(jobId, mode) {
  sessionStorage.setItem(TRADOVATE_SYNC_STORAGE_KEY, JSON.stringify({
    jobId,
    mode: mode === 'demo' ? 'demo' : 'live',
    startedAt: Date.now(),
  }));
}

function clearStoredTradovateSyncJob() {
  sessionStorage.removeItem(TRADOVATE_SYNC_STORAGE_KEY);
}

export default function ClientReporting({
  isElevated,
  showClientReportingAdmin = false,
  userId = null,
  userEmail,
  pageBadge = null,
  pageDescription = '',
  requiresProUpgrade = false,
  onUpgrade = null,
}) {
  const cacheRef = useRef(undefined);
  if (cacheRef.current === undefined) {
    cacheRef.current = requiresProUpgrade ? null : readClientReportingCache(userEmail);
  }
  const cache = cacheRef.current;

  const [clients, setClients] = useState(() => (
    requiresProUpgrade ? [] : (cache?.clients || [])
  ));
  const [selectedClientIds, setSelectedClientIds] = useState(() => cache?.selectedClientIds || []);
  const [dateRange, setDateRange] = useState(() => cache?.dateRange || 'All');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [chartData, setChartData] = useState([]);
  const [seriesRaw, setSeriesRaw] = useState({});
  const [seriesOrder, setSeriesOrder] = useState([]);
  const [seriesQuality, setSeriesQuality] = useState({});
  const [chartTab, setChartTab] = usePersistentTab('myPortfolio.chartTab', 'equity', CHART_TABS.map((t) => t.id));
  const [displayChartData, setDisplayChartData] = useState(() => cache?.chartData || []);
  const [displaySeriesRaw, setDisplaySeriesRaw] = useState(() => cache?.seriesRaw || {});
  const [displaySeriesOrder, setDisplaySeriesOrder] = useState(() => cache?.seriesOrder || []);
  const [displaySeriesQuality, setDisplaySeriesQuality] = useState(() => cache?.seriesQuality || {});
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [summariesById, setSummariesById] = useState({});
  const [sparklinesById, setSparklinesById] = useState({});
  const [initialLoading, setInitialLoading] = useState(() => !requiresProUpgrade && !cache);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialBroker, setModalInitialBroker] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  // Dashboard / Admin survive a refresh; My Portfolio ('detail') comes back
  // through its ?account= link instead, since it needs the account id.
  const [view, setView] = usePersistentTab('myPortfolio.view', 'dashboard', ['dashboard', 'admin']);
  const [detailClientId, setDetailClientId] = useState(null);
  const [chartLineColors, setChartLineColors] = useState(DEFAULT_CLIENT_REPORTING_CHART_COLORS);
  const [tradovateSyncProgress, setTradovateSyncProgress] = useState(null);
  const [tradovateSyncDismissed, setTradovateSyncDismissed] = useState(false);
  const tradovateSyncModeRef = useRef('live');
  const tradovateDirectSyncAttemptedRef = useRef(false);
  /** One-shot ?account= deep link on cold load — not dashboard row selection. */
  const accountDeepLinkRef = useRef(
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('account')
      : null,
  );
  const [viewedClient, setViewedClient] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  // Broker connection health. Drives the reconnect prompt: a lapsed session
  // makes every resync a silent no-op, which is the exact failure this page
  // used to hide.
  const [connectionHealth, setConnectionHealth] = useState(null);
  const [reauthDismissed, setReauthDismissed] = useState(false);
  const [viewingOtherHealth, setViewingOtherHealth] = useState(false);

  const {
    stockQuotes,
    worldQuotes,
    sidebarQuotesBySymbol,
    stockLoading,
    worldLoading,
    warning: sidebarQuotesWarning,
    worldRegionId,
    setWorldRegionId,
  } = useClientReportingSidebarQuotes();
  const [defaultAccountId, setDefaultAccountId] = useState(null);
  const defaultAppliedRef = useRef(false);
  const [allClients, setAllClients] = useState([]);
  const [allClientsLoaded, setAllClientsLoaded] = useState(false);
  const autoExpandedRef = useRef(false);
  const [dataRefreshGeneration, setDataRefreshGeneration] = useState(0);
  const [tradeCalendarMonthKey, setTradeCalendarMonthKey] = useState(null);

  const effectiveRange = dateRange === 'Custom' && customDateRange.start && customDateRange.end
    ? customDateRange
    : dateRange;

  const dashboardClients = useMemo(() => {
    if (!viewedClient) return clients;
    const targetId = parseTargetUserId(viewedClient);
    if (targetId && isElevated && allClients.length) {
      return allClients.filter((c) => parseTargetUserId(c) === targetId);
    }
    const ownMatch = clients.filter((c) => parseTargetUserId(c) === targetId);
    if (ownMatch.length) return ownMatch;
    return [viewedClient];
  }, [viewedClient, clients, allClients, isElevated]);

  const clientPool = useMemo(() => {
    if (isElevated && allClients.length) return allClients;
    return clients;
  }, [isElevated, allClients, clients]);

  const detailClient = useMemo(
    () => clientPool.find((client) => client.id === detailClientId)
      || dashboardClients.find((client) => client.id === detailClientId)
      || null,
    [clientPool, dashboardClients, detailClientId],
  );

  const selectedChartClients = useMemo(() => {
    const pool = viewedClient ? dashboardClients : clientPool;
    const selected = pool.filter((client) => selectedClientIds.includes(client.id));
    if (selected.length) return selected;
    if (viewedClient) return [viewedClient];
    return pool[0] ? [pool[0]] : [];
  }, [viewedClient, dashboardClients, clientPool, selectedClientIds]);

  const chartClients = useMemo(() => {
    if (view === 'detail' && detailClient) return [detailClient];
    return selectedChartClients;
  }, [view, detailClient, selectedChartClients]);

  /** Summary for every portfolio row on the dashboard (lightweight vs the main chart). */
  const summaryTargetClients = useMemo(() => {
    if (view !== 'dashboard' || !dashboardClients.length) return [];
    return dashboardClients.slice(0, MAX_PORTFOLIO_TABLE_SUMMARIES);
  }, [view, dashboardClients]);

  const selectedClientIdsKey = useMemo(
    () => selectedClientIds.join('|'),
    [selectedClientIds],
  );

  const signedInUserId = useMemo(() => {
    if (userId != null && userId !== '') return String(userId);
    const fromClient = clients.find((c) => parseTargetUserId(c));
    return fromClient ? parseTargetUserId(fromClient) : null;
  }, [userId, clients]);

  const elevatedAccountPool = useMemo(() => {
    if (isElevated && allClients.length) return allClients;
    return clients;
  }, [isElevated, allClients, clients]);

  const clientPickerOptions = useMemo(() => (
    elevatedAccountPool.map((client) => ({
      ...client,
      name: brokerAccountPickerLabel(client),
    }))
  ), [elevatedAccountPool]);

  const loadAllClients = useCallback(async () => {
    if (!isElevated) {
      setAllClients([]);
      setAllClientsLoaded(true);
      return;
    }
    setAllClientsLoaded(false);
    try {
      const res = await fetchClients(true);
      setAllClients(res.clients || []);
    } catch (e) {
      console.error('[client-reporting all-clients]', e);
    } finally {
      setAllClientsLoaded(true);
    }
  }, [isElevated]);

  const loadClients = useCallback(async () => {
    if (requiresProUpgrade) {
      setClients([]);
      setInitialLoading(false);
      return;
    }
    try {
      // Signed-in user's broker accounts; elevated tiers also load allClients for the picker.
      const res = await fetchClients(false);
      const nextClients = res.clients || [];
      setClients(nextClients);
      setSelectedClientIds((prev) => {
        const valid = prev.filter((id) => nextClients.some((client) => client.id === id));
        if (valid.length) return [valid[0]];
        if (nextClients.length >= 1) return [nextClients[0].id];
        // Elevated users may keep a cross-user selection until allClients reconciles it.
        if (isElevated && prev.length) return prev;
        return [];
      });
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setInitialLoading(false);
    }
  }, [isElevated, requiresProUpgrade]);

  const refreshReportingData = useCallback(async () => {
    await loadClients();
    setDataRefreshGeneration((generation) => generation + 1);
  }, [loadClients]);

  // When an admin is viewing someone else's portfolio, ask about THAT user.
  const healthTargetUserId = useMemo(
    () => (viewedClient ? parseTargetUserId(viewedClient) : null),
    [viewedClient],
  );

  // The account currently on screen, when it belongs to someone else: the
  // { targetUserId, broker } an admin's resync should target. Null on the
  // caller's own account (where "Resync all" refreshes every linked broker).
  const viewedAccountResync = useMemo(() => {
    if (!viewedClient) return null;
    const targetUserId = parseTargetUserId(viewedClient);
    const broker = resolveBrokerKey(viewedClient.name, viewedClient.broker);
    return targetUserId && broker ? { targetUserId, broker } : null;
  }, [viewedClient]);

  // Resync shows on the caller's own account for every tier. On someone else's
  // account only a full admin may trigger it, and only for that one account.
  const canShowResync = !viewedClient || (showClientReportingAdmin && Boolean(viewedAccountResync));

  const loadConnectionHealth = useCallback(async () => {
    try {
      const res = await fetchConnectionHealth(healthTargetUserId);
      if (!res?.success) return null;
      setConnectionHealth(res);
      setViewingOtherHealth(Boolean(res.viewingOther));
      return res;
    } catch {
      /* advisory — never block the page */
      return null;
    }
  }, [healthTargetUserId]);

  const waitForSyncResult = useCallback(async (healthBefore, { targetBroker } = {}) => {
    const beforeSyncedAt = healthBefore?.lastSyncedAt || null;
    const beforeReauth = Boolean(healthBefore?.needsReauth);
    const beforeAlpacaAttempt = healthBefore?.alpaca?.lastSyncAttemptAt || null;
    const beforeAlpacaSynced = healthBefore?.alpaca?.lastSyncedAt || null;

    for (let attempt = 0; attempt < 30; attempt += 1) {
      await new Promise((resolve) => { setTimeout(resolve, 3000); });
      const health = await loadConnectionHealth();
      if (!health) continue;

      if (health.needsReauth && !beforeReauth) {
        const broken = (health.connections || []).filter((c) => c.needsReauth);
        return {
          ok: false,
          needsReauth: true,
          error: broken.map((c) => c.message || `${c.label} needs reconnecting`).join(' · '),
        };
      }

      if (targetBroker === 'alpaca') {
        const alpaca = health.alpaca;
        if (alpaca?.lastSyncAttemptAt && alpaca.lastSyncAttemptAt !== beforeAlpacaAttempt) {
          if (alpaca.needsReauth) {
            return { ok: false, needsReauth: true, error: alpaca.message };
          }
          if (alpaca.lastSyncedAt && alpaca.lastSyncedAt !== beforeAlpacaSynced) {
            return { ok: true };
          }
          if (alpaca.lastSyncError) {
            return { ok: false, error: alpaca.lastSyncError };
          }
          return { ok: false, error: 'Alpaca sync finished without refreshing data.' };
        }
      }

      if (health.lastSyncedAt && health.lastSyncedAt !== beforeSyncedAt) {
        return { ok: true };
      }
    }

    return { ok: null };
  }, [loadConnectionHealth]);

  const resyncAll = useCallback(async () => {
    setResyncing(true);
    setError('');
    setNotice('');
    try {
      const healthBefore = await loadConnectionHealth();
      // On the caller's own account, refresh every linked broker. On an admin's
      // view of another user's account, refresh only that one account.
      const res = viewedAccountResync
        ? await resyncAccount(viewedAccountResync)
        : await resyncAllBrokers();
      if (!res?.success) {
        if (res?.needsReauth) await loadConnectionHealth();
        throw new Error(res?.error || 'Resync failed');
      }
      // Partial success is the norm when one broker's session has lapsed, so
      // report both halves rather than a bare "done".
      const failed = (res.results || []).filter((r) => !r.ok);
      setNotice(res.message || (res.queued ? 'Sync queued — your dashboard will update shortly.' : 'Resync complete.'));
      if (failed.length) {
        setError(failed.map((f) => `${f.broker}: ${f.error}`).join(' · '));
      }
      if (res.needsReauth) {
        await loadConnectionHealth();
        setReauthDismissed(false);
      } else if (res.queued || res.skipped) {
        const syncResult = await waitForSyncResult(healthBefore, {
          targetBroker: viewedAccountResync?.broker,
        });
        if (syncResult?.ok === false) {
          setError(syncResult.error || 'Sync failed');
          if (syncResult.needsReauth) setReauthDismissed(false);
        } else if (syncResult?.ok === null) {
          setNotice((prev) => (
            `${prev || res.message || ''} Sync may still be running — refresh shortly if numbers look stale.`.trim()
          ));
        }
      } else {
        await new Promise((resolve) => { setTimeout(resolve, 2500); });
        await loadConnectionHealth();
      }
      await refreshReportingData();
    } catch (e) {
      setError(e.message);
    } finally {
      setResyncing(false);
    }
  }, [refreshReportingData, loadConnectionHealth, waitForSyncResult, viewedAccountResync]);

  useEffect(() => {
    setReauthDismissed(false);
  }, [healthTargetUserId]);

  useEffect(() => {
    loadConnectionHealth();
  }, [loadConnectionHealth]);

  useEffect(() => {
    let cancelled = false;
    fetchClientReportingChartColors()
      .then((res) => {
        if (cancelled || !res?.success || !res.colors) return;
        setChartLineColors({ ...DEFAULT_CLIENT_REPORTING_CHART_COLORS, ...res.colors });
      })
      .catch(() => { /* defaults are fine */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    loadConnectionHealth();
  }, [modalOpen, loadConnectionHealth]);

  // Default portfolio: load the saved preference, then apply it once the
  // account list is known. `defaultAppliedRef` keeps a later refresh from
  // yanking the user back to the default after they pick something else.
  useEffect(() => {
    let cancelled = false;
    fetchClientReportingPrefs()
      .then((res) => {
        if (!cancelled && res?.success) setDefaultAccountId(res.defaultAccountId || null);
      })
      .catch(() => { /* preference is optional — fall back to first account */ });
    return () => { cancelled = true; };
  }, [userEmail]);

  useEffect(() => {
    if (defaultAppliedRef.current || !defaultAccountId) return;
    if (!clientPool.some((client) => client.id === defaultAccountId)) return;
    defaultAppliedRef.current = true;
    setSelectedClientIds([defaultAccountId]);
  }, [defaultAccountId, clientPool]);

  const toggleDefaultPortfolio = useCallback(async (accountId) => {
    const next = defaultAccountId === accountId ? null : accountId;
    setDefaultAccountId(next); // optimistic — this is a preference, not data
    try {
      await saveDefaultPortfolio(next);
    } catch (e) {
      setDefaultAccountId(defaultAccountId);
      setError(`Could not save default portfolio: ${e.message}`);
    }
  }, [defaultAccountId]);

  const finishTradovateSync = useCallback(async ({
    synced,
    error: syncError,
    meta,
    message,
  }) => {
    clearStoredTradovateSyncJob();
    setTradovateSyncProgress(null);
    setTradovateSyncDismissed(false);
    tradovateDirectSyncAttemptedRef.current = false;

    if (syncError) {
      setError(`Tradovate connected, but trade sync failed: ${syncError}`);
      setModalOpen(true);
    } else if ((synced ?? 0) === 0) {
      const diagnostics = meta?.diagnostics || [];
      const diag = diagnostics.find((d) => d.step === 'accounts');
      const reportDiag = diagnostics.find((d) => d.step === 'orders_report_total');
      const rejected = diagnostics
        .filter((d) => d.step === 'report_failures' || d.step === 'performance_report_failures')
        .flatMap((d) => d.failures || [])
        .filter((f) => f.status && f.status !== 200);

      setNotice('Tradovate connected — synced 0 trades.');
      setError(
        'No trade history was returned from Tradovate. '
        + (diag ? `Accounts seen: ${diag.count}. ` : '')
        + (reportDiag ? `Report chunks with data: ${reportDiag.successfulRequests || 0}. ` : '')
        + (rejected.length
          ? `Tradovate rejected the history request: ${rejected
            .slice(0, 2)
            .map((f) => `"${f.reportName}" HTTP ${f.status}${f.message ? ` — ${f.message}` : ''}`)
            .join('; ')}`
          : 'Every request succeeded but returned no rows, so the account may have no activity in the synced window.'),
      );
      setModalOpen(true);
    } else {
      setNotice(message || `Tradovate connected — synced ${synced ?? 0} trades.`);
    }

    await refreshReportingData();
  }, [refreshReportingData]);

  const runDirectTradovateSync = useCallback(async (mode) => {
    if (tradovateDirectSyncAttemptedRef.current) return null;
    tradovateDirectSyncAttemptedRef.current = true;
    const isDemo = mode === 'demo';
    setTradovateSyncProgress((prev) => ({
      ...(prev || {}),
      jobId: null,
      phase: 'history',
      message: 'Syncing trade history…',
      percent: Math.max(prev?.percent || 0, 20),
      done: false,
      error: null,
    }));
    try {
      const res = await brokerSync('ninjatrader', { isDemo });
      await finishTradovateSync({
        synced: res.synced ?? 0,
        meta: res.meta || null,
        message: res.synced
          ? `Synced ${res.synced} ${isDemo ? 'Demo' : 'Live'} NinjaTrader trades`
          : undefined,
      });
      return res;
    } catch (err) {
      await finishTradovateSync({ synced: 0, error: err.message || 'Trade sync failed' });
      return null;
    }
  }, [finishTradovateSync]);

  const beginTradovateSyncTracking = useCallback((jobId, mode = 'live') => {
    tradovateSyncModeRef.current = mode === 'demo' ? 'demo' : 'live';
    tradovateDirectSyncAttemptedRef.current = false;
    setTradovateSyncDismissed(false);
    storeTradovateSyncJob(jobId, tradovateSyncModeRef.current);
    setTradovateSyncProgress({
      jobId,
      phase: 'connecting',
      message: 'Connected — loading your trade history…',
      percent: 8,
      done: false,
    });
  }, []);

  // A sync confirmation is worth a glance, not a permanent fixture — it used to
  // sit above the Portfolios table until the next action replaced it. Errors
  // deliberately have no timer: they need the user to do something.
  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), NOTICE_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => { loadClients(); }, [loadClients]);

  useEffect(() => { loadAllClients(); }, [loadAllClients]);

  useEffect(() => {
    if (!isElevated || initialLoading || !allClients.length) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('account')) return;

    const currentId = selectedClientIds[0] || null;
    const validInPool = currentId && (
      allClients.some((client) => client.id === currentId)
      || clients.some((client) => client.id === currentId)
    );

    let nextId = null;
    let nextViewedClient = null;

    if (validInPool) {
      nextId = currentId;
      const client = allClients.find((entry) => entry.id === currentId)
        || clients.find((entry) => entry.id === currentId);
      const targetId = parseTargetUserId(client);
      const viewingOther = signedInUserId && targetId && String(targetId) !== String(signedInUserId);
      nextViewedClient = viewingOther ? client : null;
    } else if (clients.length) {
      nextId = clients[0].id;
      nextViewedClient = null;
    } else {
      const first = allClients[0];
      nextId = first?.id || null;
      if (first) {
        const targetId = parseTargetUserId(first);
        const viewingOther = signedInUserId && targetId && String(targetId) !== String(signedInUserId);
        nextViewedClient = viewingOther ? first : null;
      }
    }

    if (!nextId) return;

    const viewedMatches = (nextViewedClient?.id || null) === (viewedClient?.id || null);
    if (selectedClientIds[0] === nextId && viewedMatches) return;

    setSelectedClientIds([nextId]);
    setViewedClient(nextViewedClient);
  }, [
    isElevated,
    allClients,
    clients,
    initialLoading,
    signedInUserId,
    selectedClientIds,
    viewedClient,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accountId = params.get('account');
    if (!accountId) return;

    const pool = isElevated && allClients.length ? allClients : clients;
    const match = pool.find((client) => client.id === accountId);
    if (!match) return;

    setSelectedClientIds([accountId]);
    if (isElevated && !clients.some((client) => client.id === accountId)) {
      setViewedClient(match);
    }

    const isColdDeepLink = accountDeepLinkRef.current === accountId;
    if (isColdDeepLink) accountDeepLinkRef.current = null;

    // Dashboard row selection used to write ?account= too; a background refresh
    // (Resync all) must not treat that as "open the detail page".
    if (view === 'dashboard' && !isColdDeepLink) {
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    setDetailClientId(accountId);
    setView('detail');
  }, [clients, allClients, isElevated, view, setView]);

  useEffect(() => {
    const pending = readStoredTradovateSyncJob();
    if (pending?.jobId) {
      beginTradovateSyncTracking(pending.jobId, pending.mode);
    }
  }, [beginTradovateSyncTracking]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tv = params.get('tradovate');
    const ctx = params.get('ctx');
    if (tv !== 'success' && tv !== 'error') return;

    const syncJob = params.get('syncJob');
    const tvMode = params.get('tvMode') === 'demo' ? 'demo' : 'live';
    window.history.replaceState({}, '', `${window.location.pathname}${accountQueryParam(detailClientId)}`);

    if (tv === 'success' && ctx === 'client-reporting') {
      if (syncJob) {
        beginTradovateSyncTracking(syncJob, tvMode);
      } else {
        runDirectTradovateSync(tvMode);
      }
    } else if (tv === 'error') {
      const reason = params.get('reason');
      setError(
        reason
          ? `Tradovate connection failed: ${decodeURIComponent(reason)}`
          : 'Tradovate connection failed.',
      );
      setModalOpen(true);
    }
  }, [beginTradovateSyncTracking, detailClientId, runDirectTradovateSync]);

  // Back from SnapTrade's Connection Portal (?snaptrade=connected): pull the
  // new accounts in right away. ?connect=snaptrade opens the linking panel
  // (the welcome page's "Schwab, Robinhood & more" card).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // SnapTrade swaps our return query for its own: ?status=SUCCESS&connection_id=…
    // (or status=ERROR / ABANDONED). Older links may still carry ?snaptrade=connected.
    const stStatus = String(params.get('status') || '').toUpperCase();
    const fromSnaptrade = Boolean(params.get('connection_id')) || params.has('snaptrade');
    const returned = fromSnaptrade && (stStatus === 'SUCCESS' || params.get('snaptrade') === 'connected');
    const failed = fromSnaptrade && !returned && Boolean(stStatus);
    // ?connect=all opens the broker picker; ?connect=<broker> opens that broker.
    const connectTarget = params.get('connect');
    const wantsConnect = Boolean(connectTarget);
    if (!returned && !failed && !wantsConnect) return;
    ['snaptrade', 'connect', 'status', 'connection_id', 'error_code', 'statusCode'].forEach((k) => params.delete(k));
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);

    if (failed) {
      if (stStatus !== 'ABANDONED') setError('The brokerage connection didn’t finish. Pick your broker and try again.');
      setModalInitialBroker(null);
      setModalOpen(true);
      return;
    }
    if (wantsConnect && !returned) {
      const known = ['coinbase', 'alpaca', 'ibkr', 'ninjatrader', 'snaptrade'];
      setModalInitialBroker(known.includes(connectTarget) ? connectTarget : null);
      setModalOpen(true);
      return;
    }
    let cancelled = false;
    setNotice('Brokerage linked. Pulling in your accounts…');
    // Accounts still loading their history come back flagged (historyPending);
    // the effect below keeps syncing until they're ready.
    snaptradeSync()
      .then(async (res) => {
        if (cancelled) return;
        setNotice(res.message || 'Brokerage linked.');
        await Promise.all([loadClients(), loadAllClients()]);
        refreshMyAccounts().catch(() => {});
      })
      .catch((e) => {
        if (cancelled) return;
        setNotice('');
        setError(e.message || 'Couldn’t sync your brokerage yet. Try Sync now in a minute.');
        setModalInitialBroker('snaptrade');
        setModalOpen(true);
      });
    return () => { cancelled = true; };
  // Runs once when the page opens with the parameter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A linked brokerage still preparing its history (SnapTrade builds the daily
  // balance history a few minutes after linking): sync every 45 seconds until
  // it's ready, for up to ~8 minutes, then leave it to the scheduled sync.
  const historyRetriesRef = useRef(0);
  const historyPending = clients.some((c) => c.historyPending);
  useEffect(() => {
    if (!historyPending) {
      historyRetriesRef.current = 0;
      return undefined;
    }
    if (historyRetriesRef.current >= 10) return undefined;
    const timer = window.setTimeout(async () => {
      historyRetriesRef.current += 1;
      try {
        await snaptradeSync();
        await Promise.all([loadClients(), loadAllClients()]);
        refreshMyAccounts().catch(() => {});
      } catch (_e) { /* next pass or the scheduled sync */ }
    }, 45000);
    return () => window.clearTimeout(timer);
  // clients changes after every pass, which schedules the next one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPending, clients]);

  useEffect(() => {
    if (!tradovateSyncProgress?.jobId || tradovateSyncProgress.done) return undefined;

    let cancelled = false;
    let timer = null;
    let pollAttempts = 0;
    const startedAt = Date.now();

    const poll = async () => {
      if (cancelled) return;
      pollAttempts += 1;

      if (Date.now() - startedAt > TRADOVATE_SYNC_STALE_MS) {
        await runDirectTradovateSync(tradovateSyncModeRef.current);
        return;
      }

      try {
        const res = await fetchNinjaTraderSyncJob(tradovateSyncProgress.jobId);
        if (cancelled) return;

        if (!res?.success || !res.job) {
          if (pollAttempts >= 3 || res?.error === 'Sync job not found') {
            await runDirectTradovateSync(tradovateSyncModeRef.current);
          } else {
            timer = window.setTimeout(poll, 1500);
          }
          return;
        }

        const job = res.job;
        setTradovateSyncProgress((prev) => ({
          ...prev,
          phase: job.phase,
          message: job.message,
          percent: job.percent ?? prev?.percent ?? 0,
          done: Boolean(job.done),
          error: job.error || null,
          synced: job.synced,
        }));

        if (job.done) {
          await finishTradovateSync({
            synced: job.synced,
            error: job.error,
            meta: job.meta,
            message: job.message,
          });
          return;
        }

        timer = window.setTimeout(poll, 1200);
      } catch (_err) {
        if (!cancelled) {
          if (pollAttempts >= 5) {
            await runDirectTradovateSync(tradovateSyncModeRef.current);
          } else {
            timer = window.setTimeout(poll, 2000);
          }
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    tradovateSyncProgress?.jobId,
    tradovateSyncProgress?.done,
    finishTradovateSync,
    runDirectTradovateSync,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadPerformance() {
      if (!chartClients.length) {
        setChartData([]);
        setSeriesRaw({});
        setSeriesOrder([]);
        setSeriesQuality({});
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      const tryLoad = async (range) => fetchMergedClientPerformance(chartClients, range);

      try {
        let result = await tryLoad(effectiveRange);

        if (
          !result.hasData
          && effectiveRange === '1Y'
          && !autoExpandedRef.current
        ) {
          autoExpandedRef.current = true;
          setDateRange('All');
          result = await tryLoad('All');
        }

        if (cancelled) return;

        setChartData(result.chartData);
        setSeriesRaw(result.seriesRaw || {});
        setSeriesQuality(result.seriesQuality || {});
        setSeriesOrder(chartClients.map((client) => client.name));
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError(e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPerformance();
    return () => { cancelled = true; };
  }, [chartClients, effectiveRange, dataRefreshGeneration]);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboardData() {
      if (!summaryTargetClients.length || view !== 'dashboard') {
        setDashboardLoading(false);
        return;
      }

      setDashboardLoading(true);

      const applySummary = (clientId, summary) => {
        if (cancelled) return;
        const spark = summary?.sparkline;
        setSummariesById((prev) => ({ ...prev, [clientId]: summary }));
        setSparklinesById((prev) => ({
          ...prev,
          [clientId]: spark && Array.isArray(spark.values)
            ? spark
            : { values: [], dates: [] },
        }));
      };

      const fetchOne = async (client) => {
        try {
          const res = await fetchClientSummary(client.dataSeriesName, 'All');
          return { client, summary: res.summary || {} };
        } catch (_err) {
          return { client, summary: {} };
        }
      };

      try {
        const selectedId = selectedClientIds[0] || dashboardClients[0]?.id || null;
        const selectedClient = summaryTargetClients.find((c) => c.id === selectedId) || null;
        const otherClients = summaryTargetClients.filter((c) => c.id !== selectedId);

        if (selectedClient) {
          const { client, summary } = await fetchOne(selectedClient);
          applySummary(client.id, summary);
          if (!cancelled) setDashboardLoading(false);
        }

        if (otherClients.length) {
          const rest = await Promise.all(otherClients.map(fetchOne));
          rest.forEach(({ client, summary }) => applySummary(client.id, summary));
        }
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    }

    loadDashboardData();
    return () => { cancelled = true; };
  }, [summaryTargetClients, view, dataRefreshGeneration, selectedClientIds, dashboardClients]);

  useEffect(() => {
    autoExpandedRef.current = false;
  }, [detailClientId, selectedClientIdsKey]);

  useEffect(() => {
    if (!loading) {
      setDisplayChartData(chartData);
      setDisplaySeriesRaw(seriesRaw);
      setDisplaySeriesOrder(seriesOrder);
      setDisplaySeriesQuality(seriesQuality);
    }
  }, [loading, chartData, seriesRaw, seriesOrder, seriesQuality]);

  useEffect(() => {
    if (loading || initialLoading || !userEmail || !clients.length) return;
    writeClientReportingCache(userEmail, {
      clients,
      selectedClientIds,
      dateRange: dateRange === 'Custom' ? 'All' : dateRange,
      chartData,
      seriesRaw,
      seriesOrder,
      seriesQuality,
    });
  }, [loading, initialLoading, userEmail, clients, selectedClientIds, dateRange, chartData, seriesRaw, seriesOrder, seriesQuality]);

  const tabChartData = useMemo(
    () => buildTabChartData(displaySeriesRaw, displaySeriesOrder, chartTab),
    [chartTab, displaySeriesRaw, displaySeriesOrder],
  );
  const chartValueFormat = tabValueFormat(chartTab);

  const isChartRefreshing = loading && displayChartData.length > 0;
  const isChartInitialLoad = (loading || dashboardLoading)
    && displayChartData.length === 0
    && chartClients.length > 0;

  const displayAccounts = useMemo(
    () => displaySeriesOrder
      .map((name) => selectedChartClients.find((client) => client.name === name)
        || dashboardClients.find((client) => client.name === name))
      .filter(Boolean),
    [displaySeriesOrder, selectedChartClients, dashboardClients],
  );

  const qualityCaptions = useMemo(
    () => displaySeriesOrder
      .map((name) => ({ name, quality: displaySeriesQuality[name] }))
      .filter((entry) => entry.quality && entry.quality.quality !== 'unknown'),
    [displaySeriesOrder, displaySeriesQuality],
  );

  // Only nudge users to connect when nothing is visible to them — elevated tiers
  // can browse other members' accounts even without linking their own broker.
  const showConnectPrompt = requiresProUpgrade
    ? !initialLoading
    : (!loading
      && !initialLoading
      && allClientsLoaded
      && clientPool.length === 0);
  const handleConnectAction = useCallback(() => {
    if (requiresProUpgrade) {
      onUpgrade?.('Upgrade to RiskLo Pro to access My Portfolio');
      return;
    }
    setModalOpen(true);
  }, [requiresProUpgrade, onUpgrade]);
  const connectPromptTitle = requiresProUpgrade ? 'Upgrade to Pro' : 'Add your account';
  const connectPromptChartMessage = requiresProUpgrade
    ? 'Upgrade to link brokerages and see your equity curve, returns, and performance here.'
    : 'Connect a brokerage to see your equity curve.';
  const connectPromptDonutMessage = requiresProUpgrade
    ? 'Upgrade to see your holdings, cash, and invested capital breakdown.'
    : 'Connect a brokerage to see your holdings, cash, and invested capital breakdown.';
  const connectPromptActionLabel = requiresProUpgrade ? 'Upgrade to RiskLo Pro' : 'Add / Sync Trades';
  const connectPromptTradesMessage = requiresProUpgrade
    ? 'Upgrade to see recent trades, reconciliation, and how each day contributed to your P&L.'
    : 'Connect a brokerage to see your recent trades and reconciliation here.';
  const connectPromptCalendarMessage = requiresProUpgrade
    ? 'Upgrade to see your daily P&L calendar and spot which days drove your results.'
    : 'Connect a brokerage to see your daily P&L calendar here.';
  const myPortfolioTip = useHoverTip({ width: 280, height: 56 });

  const activeChartBrokerKey = useMemo(() => {
    const primary = displayAccounts[0]
      || selectedChartClients[0]
      || dashboardClients.find((client) => client.id === selectedClientIds[0])
      || null;
    return resolveBrokerKey(primary?.name, primary?.broker);
  }, [displayAccounts, selectedChartClients, dashboardClients, selectedClientIds]);

  const chartColorStyle = useMemo(
    () => chartColorsToCssVars(chartLineColors, activeChartBrokerKey),
    [chartLineColors, activeChartBrokerKey],
  );

  const chartProps = {
    chartTabs: CHART_TABS,
    chartTab,
    onChartTabChange: setChartTab,
    dateRange,
    onDateRangeChange: setDateRange,
    customDateRange,
    onCustomDateRangeChange: setCustomDateRange,
    tabChartData,
    displaySeriesOrder,
    displayAccounts,
    chartValueFormat,
    effectiveRange,
    isChartInitialLoad,
    isChartRefreshing,
    qualityCaptions,
    quotesBySymbol: sidebarQuotesBySymbol,
    quotesLoading: stockLoading || worldLoading,
    quotesWarning: sidebarQuotesWarning,
    seriesRaw: displaySeriesRaw,
    seriesOrder: displaySeriesOrder,
    youLabel: 'You',
    summariesById,
    showConnectPrompt,
    onAddAccount: handleConnectAction,
    connectPromptTitle,
    connectPromptMessage: connectPromptChartMessage,
    connectPromptActionLabel,
    chartLineColors,
  };

  const openPortfolio = useCallback((clientId) => {
    const match = dashboardClients.find((c) => c.id === clientId)
      || clientPool.find((c) => c.id === clientId)
      || null;
    if (match && viewedClient) {
      setViewedClient(match);
    }
    setDetailClientId(clientId);
    setView('detail');
    setSelectedClientIds([clientId]);
    window.history.replaceState({}, '', `${window.location.pathname}${accountQueryParam(clientId)}`);
  }, [dashboardClients, clientPool, viewedClient, setView]);

  /**
   * Point the dashboard at one portfolio WITHOUT leaving the dashboard.
   *
   * Clicking a row used to jump straight to the detail page, which made the
   * table a navigation menu rather than a selector — you could not use it to
   * compare accounts in place. Selecting is the common action; opening the
   * detail page is the occasional one, so it gets its own explicit control.
   */
  const focusPortfolio = useCallback((clientId) => {
    const match = dashboardClients.find((c) => c.id === clientId)
      || clientPool.find((c) => c.id === clientId)
      || null;
    if (match && viewedClient) setViewedClient(match);
    setSelectedClientIds([clientId]);
  }, [dashboardClients, clientPool, viewedClient]);

  const selectPageAccount = useCallback((account) => {
    if (!account?.id) return;
    const targetUserId = parseTargetUserId(account);
    const client = allClients.find((entry) => entry.id === account.id)
      || clients.find((entry) => entry.id === account.id)
      || {
        id: account.id,
        name: account.name,
        accountType: 'Individual',
        strategy: account.broker,
        dataSeriesName: account.dataSeriesName,
        broker: account.broker,
        targetUserId,
      };
    setViewedClient(client);
    setSelectedClientIds([account.id]);
    setView('dashboard');
    setDetailClientId(null);
    window.history.replaceState({}, '', window.location.pathname);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [clients, allClients, setView]);

  const pickerSelectedClient = useMemo(() => {
    const id = selectedClientIds[0];
    if (!id) return null;
    return elevatedAccountPool.find((c) => c.id === id) || viewedClient;
  }, [selectedClientIds, viewedClient, elevatedAccountPool]);

  const closePortfolio = useCallback(() => {
    setView('dashboard');
    setDetailClientId(null);
    window.history.replaceState({}, '', window.location.pathname);
  }, [setView]);

  const goToDashboard = useCallback(() => {
    closePortfolio();
  }, [closePortfolio]);

  const goToMyPortfolio = useCallback(() => {
    // Keep the account currently on screen — including when an admin is viewing
    // someone else's dashboard. The old path cleared viewedClient and fell back
    // to the signed-in user's own clients[0], which is why "My Portfolio" jumped
    // away from the account you were just looking at.
    const targetId = selectedClientIds[0]
      || dashboardClients[0]?.id
      || clients[0]?.id;
    if (!targetId) return;
    openPortfolio(targetId);
  }, [selectedClientIds, dashboardClients, clients, openPortfolio]);

  const goToAdminTab = useCallback(() => {
    setView('admin');
    setDetailClientId(null);
    window.history.replaceState({}, '', window.location.pathname);
  }, [setView]);

  const handleChartColorsSaved = useCallback((colors) => {
    setChartLineColors({ ...DEFAULT_CLIENT_REPORTING_CHART_COLORS, ...colors });
  }, []);

  useEffect(() => {
    if (view === 'admin' && !showClientReportingAdmin) {
      setView('dashboard');
      setDetailClientId(null);
    }
  }, [view, showClientReportingAdmin, setView]);

  const hasOwnPortfolio = clients.length > 0;
  const canOpenPortfolioTab = hasOwnPortfolio || dashboardClients.length > 0;

  if (initialLoading) {
    return (
      <div className="vd-client-reporting">
        <div className="vd-client-reporting-skeleton">
          <div className="vd-toolbar-skeleton" />
          <div className="vd-panel vd-panel-skeleton">
            <div className="vd-chart-skeleton" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vd-client-reporting" style={chartColorStyle}>
      {tradovateSyncProgress && !tradovateSyncProgress.error && !tradovateSyncDismissed && (
        <div className="vd-tradovate-sync-banner" role="status" aria-live="polite">
          <div className="vd-tradovate-sync-banner-body">
            <strong>Importing trade history</strong>
            <span className="vd-tradovate-sync-message">{tradovateSyncProgress.message}</span>
            <div className="vd-tradovate-sync-bar" aria-hidden="true">
              <div
                className="vd-tradovate-sync-bar-fill"
                style={{ width: `${Math.max(8, Math.min(100, tradovateSyncProgress.percent || 0))}%` }}
              />
            </div>
            <span className="vd-muted vd-tradovate-sync-hint">
              Your dashboard will update automatically when sync finishes — no refresh needed.
            </span>
          </div>
          <button
            type="button"
            className="vd-btn vd-btn-ghost vd-tradovate-sync-dismiss"
            onClick={() => setTradovateSyncDismissed(true)}
          >
            Continue browsing
          </button>
        </div>
      )}

      <div className="vd-cr-titlebar">
        <PageHeading
          title="My Portfolio"
          badge={pageBadge}
          description={pageDescription}
          className="vd-cr-titlebar-heading"
        />
      </div>

      <div className="vd-cr-toolbar">
        <nav className="vd-tabs vd-cr-nav" aria-label="My Portfolio views">
          <button
            type="button"
            className={view === 'dashboard' ? 'active' : ''}
            onClick={goToDashboard}
          >
            Dashboard
          </button>
          <span
            className={`vd-cr-nav-tab-wrap${canOpenPortfolioTab ? '' : ' is-locked'}`}
            {...(!canOpenPortfolioTab ? myPortfolioTip.hoverProps('my-portfolio') : {})}
          >
            <button
              type="button"
              className={view === 'detail' ? 'active' : ''}
              onClick={goToMyPortfolio}
              disabled={!canOpenPortfolioTab}
              aria-disabled={!canOpenPortfolioTab}
            >
              My Portfolio
            </button>
          </span>
          {showClientReportingAdmin ? (
            <button
              type="button"
              className={view === 'admin' ? 'active' : ''}
              onClick={goToAdminTab}
            >
              Admin
            </button>
          ) : null}
        </nav>

        {view !== 'admin' ? (
        <ClientAccountPicker
          className="vd-cr-toolbar-account vd-client-select-full"
          options={clientPickerOptions}
          value={selectedClientIds}
          selectedOption={pickerSelectedClient ? {
            ...pickerSelectedClient,
            name: brokerAccountPickerLabel(pickerSelectedClient),
          } : null}
          onChange={(ids) => {
            const nextId = ids[0] || null;
            if (!nextId) {
              setViewedClient(null);
              setSelectedClientIds([]);
              return;
            }
            const nextClient = elevatedAccountPool.find((c) => c.id === nextId) || null;
            if (isElevated && allClients.length && nextClient && signedInUserId) {
              const nextTargetId = parseTargetUserId(nextClient);
              setViewedClient(
                nextTargetId && String(nextTargetId) !== String(signedInUserId)
                  ? nextClient
                  : null,
              );
            } else {
              setViewedClient(null);
            }
            setSelectedClientIds([nextId]);
            if (view === 'detail' && nextId) {
              setDetailClientId(nextId);
              window.history.replaceState({}, '', `${window.location.pathname}${accountQueryParam(nextId)}`);
            }
          }}
          placeholder={elevatedAccountPool.length ? 'Select account' : 'No accounts yet'}
        />
        ) : null}

        {view !== 'admin' ? (
        <div className="vd-cr-toolbar-sync-actions">
          {/* Resync shows on your own account for every tier; on another user's
              account only a full admin sees it, and it resyncs just that
              account. "Add / Sync Trades" is unchanged. */}
          {!requiresProUpgrade && canShowResync ? (
          <div className="vd-client-resync-wrap">
            {/* Above the button and out of flow: the label appears only once
                the sync time is known, and in flow it would shove the button
                out of line with the rest of the toolbar the moment it loaded. */}
            {resyncing ? (
              <span className="vd-client-last-synced">Syncing…</span>
            ) : connectionHealth?.lastSyncedAt ? (
              <span
                className="vd-client-last-synced"
                title={new Date(connectionHealth.lastSyncedAt).toLocaleString()}
              >
                Synced {formatLastSynced(connectionHealth.lastSyncedAt)}
              </span>
            ) : null}
            <button
              type="button"
              className="vd-btn vd-client-resync-btn"
              onClick={resyncAll}
              disabled={resyncing}
              title={viewedAccountResync ? 'Refresh this account' : 'Refresh every connected brokerage'}
            >
              {resyncing ? (
                'Resyncing…'
              ) : (
                <>
                  <span className="vd-cr-btn-label-long">
                    {viewedAccountResync ? 'Resync account' : 'Resync all'}
                  </span>
                  <span className="vd-cr-btn-label-short">Resync</span>
                </>
              )}
            </button>
          </div>
          ) : null}
          <button type="button" className="vd-btn vd-client-sync-btn" onClick={handleConnectAction}>
            <span className="vd-cr-btn-label-long">{connectPromptActionLabel}</span>
            <span className="vd-cr-btn-label-short">{requiresProUpgrade ? 'Upgrade' : 'Add trades'}</span>
          </button>
        </div>
        ) : null}
      </div>

      {!hasOwnPortfolio && !requiresProUpgrade ? (
        <HoverTip position={myPortfolioTip.position} width={280} className="vd-cr-nav-tip">
          Link your brokerage account to see your portfolio.
        </HoverTip>
      ) : null}

      {/* Sits inline above the data rather than in a toast: a toast dismisses
          itself, and this condition persists until the user acts on it. */}
      {reauthDismissed ? null : (
        <ClientReportingReauthBanner
          health={connectionHealth}
          viewingOther={viewingOtherHealth}
          onDismiss={() => setReauthDismissed(true)}
          onUpdateAlpacaKeys={() => {
            setModalInitialBroker('alpaca');
            setModalOpen(true);
          }}
        />
      )}

      <ClientReportingToasts
        notice={notice}
        error={error}
        onDismissNotice={() => setNotice('')}
        onDismissError={() => setError('')}
      />

      {view === 'dashboard' ? (
        <>
          {/* Names the portfolio the numbers below belong to. The account
              picker sits in the far corner of the toolbar, so the same chart
              could be any of several accounts with nothing nearby saying
              which. */}
          <ClientReportingDashboardHeader
            clients={selectedChartClients}
            chartData={chartData}
            health={connectionHealth}
          />

          {/* Patterns in the person's own trades; never while viewing someone else's. */}
          {!viewedClient && !showConnectPrompt ? <TradeInsightsPanel refreshKey={dataRefreshGeneration} /> : null}

          <div className="vd-ml-dashboard">
            <div className="vd-ml-main">
              <ClientReportingPortfolioTable
                clients={dashboardClients}
                summariesById={summariesById}
                sparklinesById={sparklinesById}
                loading={dashboardLoading}
                onSelectPortfolio={focusPortfolio}
                onOpenPortfolio={openPortfolio}
                selectedIds={selectedClientIds}
                defaultAccountId={defaultAccountId}
                onToggleDefault={toggleDefaultPortfolio}
                onAddAccount={handleConnectAction}
                connectActionLabel={connectPromptActionLabel}
              />
              <ClientReportingChartPanel {...chartProps} />
              <div className="vd-ml-main-stack">
                {/* Between the curve and the trade list: the curve says how the
                    account did, this says which days did it. */}
                <ClientReportingTradeCalendar
                  clients={dashboardClients}
                  selectedClientId={selectedClientIds[0] || dashboardClients[0]?.id || null}
                  refreshKey={dataRefreshGeneration}
                  monthKey={tradeCalendarMonthKey}
                  onMonthKeyChange={setTradeCalendarMonthKey}
                  showDemoPreview={showConnectPrompt}
                  onConnectAction={handleConnectAction}
                  connectPromptTitle={connectPromptTitle}
                  connectPromptMessage={connectPromptCalendarMessage}
                  connectPromptActionLabel={connectPromptActionLabel}
                />
                <ClientReportingRecentTrades
                  clients={dashboardClients}
                  selectedClientId={selectedClientIds[0] || dashboardClients[0]?.id || null}
                  refreshKey={dataRefreshGeneration}
                  summary={summariesById[selectedClientIds[0] || dashboardClients[0]?.id || ''] || null}
                  monthKey={tradeCalendarMonthKey}
                  onMonthKeyChange={setTradeCalendarMonthKey}
                  showDemoPreview={showConnectPrompt}
                  onConnectAction={handleConnectAction}
                  connectPromptTitle={connectPromptTitle}
                  connectPromptMessage={connectPromptTradesMessage}
                  connectPromptActionLabel={connectPromptActionLabel}
                />
              </div>
            </div>

            <aside className="vd-ml-sidebar">
              <ClientReportingEquityDonut
                clients={dashboardClients}
                summariesById={summariesById}
                loading={dashboardLoading}
                showConnectPrompt={showConnectPrompt}
                onAddAccount={handleConnectAction}
                connectPromptTitle={connectPromptTitle}
                connectPromptMessage={connectPromptDonutMessage}
                connectPromptActionLabel={connectPromptActionLabel}
              />
              <ClientReportingStocks
                quotesBySymbol={stockQuotes}
                loading={stockLoading}
                warning={sidebarQuotesWarning}
              />
              <ClientReportingWorldMarkets
                quotesBySymbol={worldQuotes}
                loading={worldLoading}
                warning={sidebarQuotesWarning}
                regionId={worldRegionId}
                onRegionChange={setWorldRegionId}
              />
              <ClientReportingMarketHours />
            </aside>
          </div>
        </>
      ) : view === 'detail' ? (
        <ClientReportingPortfolioDetail
          client={detailClient}
          clients={dashboardClients}
          onSelectClient={openPortfolio}
          onBack={closePortfolio}
          effectiveRange={effectiveRange}
          chartProps={chartProps}
          refreshKey={dataRefreshGeneration}
        />
      ) : (
        <ClientReportingChartColorsAdmin onSaved={handleChartColorsSaved} />
      )}

      {isElevated && view === 'dashboard' && (
        <AdminAccountsOverview
          activeAccountId={selectedClientIds[0] || viewedClient?.id || null}
          onSelectAccount={selectPageAccount}
        />
      )}

      {!requiresProUpgrade ? (
        <TradeIntegrationModal
          isOpen={modalOpen}
          initialBrokerId={modalInitialBroker}
          onClose={() => {
            setModalOpen(false);
            setModalInitialBroker(null);
            loadClients();
            loadAllClients();
            loadConnectionHealth();
          }}
          onSynced={() => {
            loadClients();
            loadAllClients();
            loadConnectionHealth();
          }}
        />
      ) : null}
    </div>
  );
}
